import { type Maybe, ensure, hasValue, sum } from '@vrplatform/utils';
import { getTransactionLineType } from '@vrplatform/ui-common';
import type {
  DepositFormInputs,
  DepositReservationLine,
} from './_hooks/useDepositForm';
import {
  getDepositManualLinesCentTotal,
  getDepositReservationLinesCentTotal,
} from './_utils';
import type { ResponseData } from '@vrplatform/data';

type Extras = {
  transactionId: string;
  uniqueRef: Maybe<string>;
};

type Line = ResponseData<'get:/transactions/{id}'>['lines'][number] & {
  accountId?: string | null;
  reservationId?: string | null;
  listingId?: string | null;
  contactId?: string | null;
};

export const mapApiDepositInitialLines = (
  lines: Line[],
  transactionId: string
) =>
  lines.map((line) => {
    const assignment = line.assignment;

    return {
      transactionLineId: line.id,
      assignment: assignment,
      centTotal: line.amount,
      description: line.description || '',
      uniqueRef: line.uniqueRef,
      accountId: line.account?.id || line.accountId || null,
      reservationId: line.reservation?.id || line.reservationId || null,
      listingId: line.listing?.id || line.listingId || null,
      party: line.party,
      contactId: line.contact?.id || line.contactId || null,
      type: getTransactionLineType({
        accountAssignmentType: assignment,
        centTotal: line.amount,
      }),
      transactionId,
      matchLineTypeClassification: line.matchLineTypeClassification,
      matchReservationConfirmationCode: line.matchReservationConfirmationCode,
      matchReservationStripeGuestRef: line.matchReservationStripeGuestRef,
    };
  });

export const mapApiDepositLines = (
  transaction: ResponseData<'get:/transactions/{id}'>
) => {
  const transactionId = transaction.id;

  // Map API lines to internal format matching getDepositInitialLines output
  const lines = mapApiDepositInitialLines(transaction.lines, transactionId);

  const linesWithReservation = lines.filter((x) => x.reservationId);

  const reduceLines = (l: typeof lines) =>
    l.reduce<typeof lines>((acc, line) => {
      const reservationId = line.reservationId;
      const index = acc.findIndex((x) => x.reservationId === reservationId);
      if (index === -1) acc.push({ ...line });
      else acc[index].centTotal = acc[index].centTotal + line.centTotal;
      return acc;
    }, []);

  const payment_lines = reduceLines(
    linesWithReservation.filter(
      (x) => x.type === 'payment' || x.type === 'refund'
    )
  );

  const merchantFee_lines = reduceLines(
    linesWithReservation.filter((x) => x.type === 'merchantFee')
  );

  const channelFee_lines = reduceLines(
    linesWithReservation.filter((x) => x.type === 'channelFee')
  );

  const reserve_lines = reduceLines(
    linesWithReservation.filter((x) => x.type === 'reserve')
  );

  const vat_lines = reduceLines(
    linesWithReservation.filter((x) => x.type === 'vat')
  );

  const coHost_lines = reduceLines(
    linesWithReservation.filter((x) => x.type === 'coHostPayout')
  );

  const uniqueReservationIds = [
    ...new Set(
      [
        ...payment_lines,
        ...merchantFee_lines,
        ...channelFee_lines,
        ...reserve_lines,
        ...vat_lines,
        ...coHost_lines,
      ]
        .map((x) => x.reservationId)
        .filter(hasValue)
    ),
  ];

  const reservations = uniqueReservationIds.map<
    DepositReservationLine & Extras
  >((reservationId) => {
    const payment = payment_lines.find(
      (x) => x.reservationId === reservationId
    );
    const merchantFee = merchantFee_lines.find(
      (x) => x.reservationId === reservationId
    );

    const reserve = reserve_lines.find(
      (x) => x.reservationId === reservationId
    );

    const channelFee = channelFee_lines.find(
      (x) => x.reservationId === reservationId
    );

    const vat = vat_lines.find((x) => x.reservationId === reservationId);

    const coHost = coHost_lines.find((x) => x.reservationId === reservationId);

    const transactionLineId = (payment?.transactionLineId ||
      reserve?.transactionLineId ||
      merchantFee?.transactionLineId ||
      channelFee?.transactionLineId ||
      vat?.transactionLineId ||
      coHost?.transactionLineId)!;

    return {
      transactionLineId,
      reservationId,
      paymentLineId: payment?.transactionLineId ?? null,
      reserveLineId: reserve?.transactionLineId ?? null,
      merchantFeeLineId: merchantFee?.transactionLineId ?? null,
      channelFeeLineId: channelFee?.transactionLineId ?? null,
      vatLineId: vat?.transactionLineId ?? null,
      coHostLineId: coHost?.transactionLineId ?? null,
      uniqueRef:
        payment?.uniqueRef ||
        reserve?.uniqueRef ||
        merchantFee?.uniqueRef ||
        channelFee?.uniqueRef ||
        vat?.uniqueRef ||
        coHost?.uniqueRef,
      paymentCentTotal: payment?.centTotal ?? 0,
      paymentDescription: payment?.description || '',
      reserveCentTotal: reserve?.centTotal ?? 0,
      reserveParty: reserve?.party ?? null,
      reserveDescription: reserve?.description || '',
      merchantFeeCentTotal: merchantFee?.centTotal ?? 0,
      channelFeeCentTotal: channelFee?.centTotal ?? 0,
      channelFeeDescription: channelFee?.description || '',
      merchantFeeDescription: merchantFee?.description || '',
      transactionId,
      paymentLineType: payment?.matchLineTypeClassification || null,
      reserveLineType: reserve?.matchLineTypeClassification || null,
      merchantFeeLineType: merchantFee?.matchLineTypeClassification || null,
      channelFeeLineType: channelFee?.matchLineTypeClassification || null,
      vatCentTotal: vat?.centTotal ?? 0,
      vatLineType: vat?.matchLineTypeClassification || null,
      vatDescription: vat?.description || '',
      coHostCentTotal: coHost?.centTotal ?? 0,
      coHostLineType: coHost?.matchLineTypeClassification || null,
      coHostDescription: coHost?.description || '',
    };
  });

  const manualMerchantFeeLines = lines.filter(
    (x) => !x.reservationId && x.assignment === 'deposit_merchantFee'
  );

  const manuals = lines
    .filter((x) => !x.reservationId && x.assignment !== 'deposit_merchantFee')
    .map((x) => {
      const merchantIndex = manualMerchantFeeLines.findIndex(
        (a) => x.contactId === a.contactId && x.party === a.party
      );

      const merchantFeeLine = manualMerchantFeeLines.splice(
        merchantIndex,
        1
      )[0];

      return ensure<DepositFormInputs['manuals'][0] & Extras>({
        transactionLineId: x.transactionLineId,
        paymentLineId: x.transactionLineId,
        merchantFeeLineId: merchantFeeLine?.transactionLineId ?? null,
        description: x.description,
        uniqueRef: x.uniqueRef,
        listingId: x.listingId || null,
        accountId: x.accountId || null,
        party: x.party || 'manager',
        merchantFeeCentTotal: merchantFeeLine?.centTotal ?? 0,
        merchantFeeLineType:
          merchantFeeLine?.matchLineTypeClassification || null,
        contactId: x.contactId || null,
        transactionId: x.transactionId,
        accountAssignmentType: (x.assignment || null) as any,
        paymentCentTotal: x.centTotal,
        reservationId: x.reservationId,
        paymentLineType: x.matchLineTypeClassification || null,
      });
    });

  for (const x of manualMerchantFeeLines) {
    manuals.push(
      ensure<DepositFormInputs['manuals'][0] & Extras>({
        transactionLineId: x.transactionLineId,
        paymentLineId: null,
        merchantFeeLineId: x.transactionLineId,
        description: x.description,
        uniqueRef: x.uniqueRef,
        listingId: x.listingId || null,
        accountId: x.accountId || null,
        party: x.party || 'owners',
        merchantFeeCentTotal: x?.centTotal ?? 0,
        merchantFeeLineType: x?.matchLineTypeClassification || null,
        contactId: x.contactId || null,
        transactionId: x.transactionId,
        accountAssignmentType: (x.assignment || null) as any,
        paymentCentTotal: 0,
        reservationId: x.reservationId,
        paymentLineType: null,
      })
    );
  }

  const manualLineCentTotal = getDepositManualLinesCentTotal(manuals);

  const reservationLinesCentTotal =
    getDepositReservationLinesCentTotal(reservations);

  const unassignedLines = linesWithReservation.filter(
    (x) => !x.type && !x.accountId
  );

  const unassignedLineCentTotal = sum(unassignedLines, 'centTotal');

  return {
    totals: {
      reservationLinesCentTotal,
      manualLineCentTotal,
      unassignedLineCentTotal,
      centTotal:
        manualLineCentTotal +
        reservationLinesCentTotal +
        unassignedLineCentTotal,
    },
    lines: {
      manuals,
      reservations,
      unassignedLines,
    },
  };
};
